import { useMutation } from '@apollo/client';
import { AlertService, Button, Snackbar, Timing } from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { UpdateTrucklaneMinimums } from '../../../graphql/mutations';
import { initializeStateFromDB, minimumReducer } from '../smart-award-settings/utils';
import SmartAwardSettingsSteps from './smart-award-settings-steps';

const useStyles = createUseStyles({
  wrapper: {
    padding: '24px',
  },
  saveSettings: {
    display: 'flex',
    justifyContent: 'flex-end',
    boxShadow: 'inset 0px 1px 0 0 #d9dee1',
    padding: '24px',
    width: '100%',
    '& button': {
      marginLeft: 8,
    },
  },
});

const SmartAwardSettings = ({ user, cancellable, saveText, onClose, onSave, style, refetchQueries }) => {
  const classes = useStyles();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [touched, setTouched] = useState(false);

  const [userMinimums, setUserMinimums] = useReducer(minimumReducer, null, () => initializeStateFromDB(user.site.trucklaneMinimums));

  const [useWeightMinimum, setUseWeightMinimum] = useState(
    userMinimums.deliveryWeight.dry > 0 ||
      userMinimums.deliveryWeight.refrigerated > 0 ||
      userMinimums.deliveryWeight.frozen > 0 ||
      userMinimums.pickupWeight.dry > 0 ||
      userMinimums.pickupWeight.refrigerated > 0 ||
      userMinimums.pickupWeight.frozen > 0,
  );
  const [usePalletMinimum, setUsePalletMinimum] = useState(
    userMinimums.deliveryPallets.dry > 0 ||
      userMinimums.deliveryPallets.refrigerated > 0 ||
      userMinimums.deliveryPallets.frozen > 0 ||
      userMinimums.pickupPallets.dry > 0 ||
      userMinimums.pickupPallets.refrigerated > 0 ||
      userMinimums.pickupPallets.frozen > 0,
  );

  useEffect(() => {
    setUserMinimums({
      type: 'initializeState',
      dbState: user.site.trucklaneMinimums,
    });
  }, [user.site.trucklaneMinimums]);

  const [updateMinimums, { loading: settingMinimums }] = useMutation(UpdateTrucklaneMinimums);

  const setMinimum = Timing.debounce((term, truckType, value) => {
    setUserMinimums({
      type: 'setMinimum',
      term,
      truckType,
      value: accounting.unformat(value),
    });
  }, 10);

  const setMode = (option) => {
    setTouched(true);
    setUserMinimums({
      type: 'setMode',
      value: option.value,
    });
  };

  const hideSuccessMessage = () => setShowSuccessMessage(false);

  const resetDisabledSettings = () => {
    const emptySettings = {
      dry: 0,
      refrigerated: 0,
      frozen: 0,
    };
    if (!useWeightMinimum) {
      userMinimums.deliveryWeight = { ...emptySettings };
      userMinimums.pickupWeight = { ...emptySettings };
    }
    if (!usePalletMinimum) {
      userMinimums.deliveryPallets = { ...emptySettings };
      userMinimums.pickupPallets = { ...emptySettings };
    }
  };

  const save = () => {
    resetDisabledSettings();
    updateMinimums({ variables: { minimums: userMinimums }, refetchQueries })
      .then((res) => {
        if (res.data.updateTrucklaneMinimums.errors && res.data.updateTrucklaneMinimums.errors.length > 0) {
          throw new Error(res.data.updateTrucklaneMinimums.errors[0].message);
        }
        if (onSave) {
          onSave();
        } else {
          setShowSuccessMessage(true);
          setTouched(false);
        }
      })
      .catch((err) => {
        AlertService.alert({
          type: 'warning',
          message: err.message,
        });
      });
  };

  return (
    <>
      <div className={`${style?.wrapper} ${classes.wrapper}`}>
        <SmartAwardSettingsSteps
          userMinimums={userMinimums}
          setTouched={setTouched}
          setMinimum={setMinimum}
          setMode={setMode}
          useWeightMinimum={useWeightMinimum}
          usePalletMinimum={usePalletMinimum}
          setUseWeightMinimum={setUseWeightMinimum}
          setUsePalletMinimum={setUsePalletMinimum}
        />
      </div>
      <div className={classes.saveSettings}>
        {cancellable && (
          <Button secondary onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button onClick={save} primary disabled={!touched} loading={settingMinimums} loadingText="Saving">
          {saveText || 'Save'}
        </Button>
      </div>
      <Snackbar show={showSuccessMessage} callback={hideSuccessMessage} message="Settings saved successfully" />
    </>
  );
};

SmartAwardSettings.propTypes = {
  user: PropTypes.object,
  cancellable: PropTypes.bool,
  saveText: PropTypes.string,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  style: PropTypes.object,
  refetchQueries: PropTypes.array,
};

export default SmartAwardSettings;
